// ./src/components/features/Events/EventDurationBadge.tsx
//
// A single, consistent "how long the event runs" pill (e.g. "3 hours",
// "2 days · overnight"). Used on the details editor and the event dashboard so
// duration looks identical everywhere. Renders nothing when there's no usable
// start/end pair.

import React from 'react';
import { Badge, BadgeProps } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { AppIcon } from '@/components/generic';
import { formatEventDuration } from '@/utils';

export interface EventDurationBadgeProps extends Omit<BadgeProps, 'children' | 'leftSection'> {
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  /**
   * The event's time zone — whose midnight makes an event "overnight" and counts its days. Pass it with STORED instants
   * (2026-10-04: a New York 5–8 PM date read "overnight" to a host in Berlin). Omit it for an editor's own picker values,
   * which are already wall clocks in that zone.
   */
  tz?: string | null;
}

export const EventDurationBadge: React.FC<EventDurationBadgeProps> = ({
  startsAt,
  endsAt,
  isAllDay,
  tz,
  color = 'grape',
  size = 'lg',
  radius = 'sm',
  ...rest
}) => {
  const { t } = useTranslation(['events']);
  const duration = formatEventDuration(startsAt ?? null, endsAt ?? null, !!isAllDay, tz ?? undefined);
  if (!duration) return null;

  return (
    <Badge
      variant="light"
      color={color}
      size={size}
      radius={radius}
      tt="none"
      leftSection={<AppIcon iconName="Clock" size={14} />}
      style={{ fontWeight: 500 }}
      {...rest}
    >
      {duration.text}
      {duration.isOvernight ? ` · ${t('events:duration.overnight', { defaultValue: 'overnight' })}` : ''}
    </Badge>
  );
};
